import { useCallback, useMemo } from 'react';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop';

import { SPACE_PERMISSIONS_SETTINGS } from '@confluence/named-routes/entry-points/routes';
import { usePageSpaceKey } from '@confluence/page-context';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';

import { PublicLinkPageStatus } from '../graphql/__types__/PublicLinkPageQuery';
import { getAdminDataSecurityPolicyUrl, getAdminGlobalPermsPublicLinksUrl } from '../utils';

import { useIsPLEmpoweredUser } from './useIsPLEmpoweredUser';

type AccessLevel = 'space' | 'site' | 'org';

const publicLinkStatusToAccessLevelMappings = {
	[PublicLinkPageStatus.SITE_DISABLED]: 'site',
	[PublicLinkPageStatus.SPACE_BLOCKED]: 'site',
	[PublicLinkPageStatus.BLOCKED_BY_PRODUCT]: 'site',
	[PublicLinkPageStatus.SPACE_DISABLED]: 'space',
	[PublicLinkPageStatus.BLOCKED_BY_SPACE]: 'space',
	[PublicLinkPageStatus.BLOCKED_BY_ORG]: 'org',
	[PublicLinkPageStatus.SITE_BLOCKED]: 'org',
	[PublicLinkPageStatus.BLOCKED_BY_CLASSIFICATION_LEVEL]: 'org',
	[PublicLinkPageStatus.BLOCKED_BY_CONTAINER_POLICY]: 'org',
};

export interface PublicLinkPrivilegeData {
	isEmpowered: boolean;
	empoweredUserSettingsLinkHref: string | undefined;
	accessLevel: AccessLevel | null;
	isSpaceAdmin: boolean | undefined;
	isSiteAdmin: boolean | undefined;
	error: ApolloErrorInterop | undefined;
}

export const usePublicLinkPrivilegeData = ({
	publicLinkStatus,
}: {
	publicLinkStatus: PublicLinkPageStatus | null;
}) => {
	const { environment, orgId } = useSessionData();
	const [spaceKey] = usePageSpaceKey();
	const spaceId = useSpaceId(spaceKey);

	const {
		isEmpoweredUser: isEmpowered,
		isSiteAdmin,
		isSpaceAdmin,
		error,
	} = useIsPLEmpoweredUser({
		publicLinkStatus,
		skip: !publicLinkStatus,
	});

	const accessLevel: AccessLevel | null = publicLinkStatus
		? publicLinkStatusToAccessLevelMappings[publicLinkStatus]
		: null;

	const adminUrls = useMemo(
		() => ({
			globalPublicLinksTab: getAdminGlobalPermsPublicLinksUrl(),
			globalPublicLinksSpaceDetails: getAdminGlobalPermsPublicLinksUrl(spaceId),
			spacePublicLinksTab:
				spaceKey &&
				SPACE_PERMISSIONS_SETTINGS.toUrl({
					spaceKey,
					tab: 'public-links',
				}),
			adminHubDataSecurityPoliciesTab: getAdminDataSecurityPolicyUrl(environment, orgId || ''),
		}),
		[spaceId, spaceKey, environment, orgId],
	);

	const getEmpoweredUserSettingsLinkHref = useCallback(
		(publicLinkStatus) => {
			if (accessLevel === 'space') {
				return isSpaceAdmin
					? adminUrls.spacePublicLinksTab
					: adminUrls.globalPublicLinksSpaceDetails;
			} else if (accessLevel === 'site') {
				switch (publicLinkStatus) {
					case PublicLinkPageStatus.SITE_DISABLED:
					case PublicLinkPageStatus.SPACE_BLOCKED:
						return adminUrls.globalPublicLinksTab;
					case PublicLinkPageStatus.BLOCKED_BY_PRODUCT:
						return adminUrls.globalPublicLinksSpaceDetails;
				}
			} else if (accessLevel === 'org') {
				return isSiteAdmin
					? adminUrls.adminHubDataSecurityPoliciesTab
					: adminUrls.globalPublicLinksSpaceDetails;
			}
		},
		[accessLevel, isSpaceAdmin, isSiteAdmin, adminUrls],
	);

	return {
		isEmpowered,
		empoweredUserSettingsLinkHref: getEmpoweredUserSettingsLinkHref(publicLinkStatus),
		accessLevel,
		isSpaceAdmin,
		isSiteAdmin,
		error,
	};
};
